"use client";

import { Box, Button, type ButtonProps } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { LuArrowRight } from "react-icons/lu";

interface InteractiveButtonProps extends ButtonProps {
  children: React.ReactNode;
}

/**
 * A button whose label slides out on hover while a second copy (with a
 * trailing arrow) slides in from the opposite side — plus a small dot that
 * grows into a soft glow behind it. Hover state is tracked in React rather
 * than relying on CSS group-hover, so it works with any Button variant in
 * this theme without per-variant color configuration.
 */
export const InteractiveButton = ({
  children,
  ...buttonProps
}: InteractiveButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnter = useCallback(() => setIsHovered(true), []);
  const onMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <Button
      position="relative"
      overflow="hidden"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...buttonProps}
    >
      <Box
        as="span"
        position="relative"
        zIndex={1}
        display="inline-flex"
        alignItems="center"
        gap={1.5}
        transitionProperty="transform, opacity"
        transitionDuration="0.3s"
        transitionTimingFunction="ease-out"
        transform={isHovered ? "translateX(12px)" : "translateX(0)"}
        opacity={isHovered ? 0 : 1}
      >
        {children}
        {/* Invisible arrow reserves width so the button fits the hover label. */}
        <Box as={LuArrowRight} boxSize={4} opacity={0} aria-hidden />
      </Box>
      <Box
        position="absolute"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1.5}
        transitionProperty="transform, opacity"
        transitionDuration="0.3s"
        transitionTimingFunction="ease-out"
        transform={isHovered ? "translateX(0)" : "translateX(-12px)"}
        opacity={isHovered ? 1 : 0}
      >
        {children}
        <Box as={LuArrowRight} boxSize={4} />
      </Box>
      <Box
        position="absolute"
        top="50%"
        left="50%"
        boxSize={isHovered ? "260%" : "0.5rem"}
        borderRadius="full"
        bg="currentColor"
        opacity={isHovered ? 0.08 : 0.2}
        transform="translate(-50%, -50%)"
        transitionProperty="width, height, opacity"
        transitionDuration="0.4s"
        transitionTimingFunction="ease-out"
        pointerEvents="none"
      />
    </Button>
  );
};
